import { ChangeEmail } from "@/components/ChangeEmail";
import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, avatarUrl } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, Briefcase } from "lucide-react";

export function ProfileDialog({ open, onOpenChange, onSaved }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [snack, setSnack] = useState("");
  const [preview, setPreview] = useState(null); // local object URL after selecting

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profile");
      setProfile(data);
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setJobTitle(data.job_title || "");
      setSnack(data.favourite_snack || "");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const fullName = `${firstName} ${lastName}`.trim();
  const initials = (fullName || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const imgSrc = preview || (profile?.has_avatar ? avatarUrl(profile.id, profile.avatar_version) : null);

  const pickFile = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be 5MB or smaller"); return; }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, has_avatar: true, avatar_version: data.avatar_version }));
      toast.success("Photo updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!(await confirmDelete({ title: "Remove your profile photo?", confirmText: "Remove" }))) return;
    setUploading(true);
    try {
      await api.delete("/profile/avatar");
      setProfile((p) => ({ ...p, has_avatar: false, avatar_version: null }));
      setPreview(null);
      toast.success("Photo removed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { first_name: firstName, last_name: lastName, job_title: jobTitle, favourite_snack: snack });
      toast.success("Profile saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md" data-testid="profile-dialog">
        <DialogHeader><DialogTitle className="font-display">Your profile</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center text-[#9FA1A4]"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="flex items-center gap-4">
              <div className="relative">
                <span className="h-20 w-20 rounded-full overflow-hidden flex items-center justify-center bg-[#06060C] text-white text-2xl font-medium" data-testid="profile-avatar">
                  {imgSrc ? <img src={imgSrc} alt={fullName} className="h-full w-full object-cover" /> : initials}
                </span>
                <button data-testid="profile-avatar-upload" onClick={pickFile} disabled={uploading} className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#723FFF] text-white flex items-center justify-center border-2 border-white hover:bg-[#5b2fd6] disabled:opacity-60">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                </button>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => upload(e.target.files?.[0])} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#06060C] truncate" data-testid="profile-email">{profile?.email}</p>
                {profile?.account === "agency" && profile?.role === "super_admin" && <ChangeEmail current={profile.email} onChanged={(email) => { setProfile((p) => ({ ...p, email })); onSaved?.(); }} />}
                {(profile?.has_avatar || preview) && (
                  <button data-testid="profile-avatar-remove" onClick={removePhoto} disabled={uploading} className="mt-1 flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"><Trash2 className="h-3 w-3" /> Remove photo</button>
                )}
                <p className="mt-1 text-[11px] text-[#9FA1A4]">JPG, PNG or WebP · max 5MB</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <Input data-testid="profile-first-name-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="First" />
              </Field>
              <Field label="Last name">
                <Input data-testid="profile-last-name-input" value={lastName} onChange={(e) => setLastName(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="Last" />
              </Field>
            </div>
            <Field label="Job title">
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9FA1A4]" />
                <Input data-testid="profile-jobtitle-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF] pl-9" placeholder="e.g. Marketing Manager" />
              </div>
            </Field>
            <Field label="Favourite snack">
              <Input data-testid="profile-snack-input" value={snack} onChange={(e) => setSnack(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="We love Tim Tams at Shuttle" />
            </Field>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="profile-save-button" onClick={save} disabled={saving || loading || !fullName} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
